/**
 * ModelComponent - Manages multiple 3D models (glTF/GLB) in the scene
 * Handles loading, positioning, and cleanup of model meshes
 * Supports multiple models per stage via Map-based storage
 */

import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { MediaItem } from '../media/MediaConfig';
import { MediaLoader } from '../media/MediaLoader';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('ModelComponent');

/** Data stored for each loaded model */
interface ModelData {
  root: TransformNode;
  meshes: AbstractMesh[];
  mediaItem: MediaItem;
}

export class ModelComponent {
  private scene: Scene;
  private mediaLoader: MediaLoader;
  private parentNode: TransformNode | null = null;

  /** Map of loaded models keyed by URL */
  private models: Map<string, ModelData> = new Map();

  /** Track the last loaded model URL for backwards compatibility */
  private lastLoadedUrl: string | null = null;

  constructor(scene: Scene) {
    this.scene = scene;
    this.mediaLoader = new MediaLoader(scene);
  }

  /**
   * Set the parent node for all model root TransformNodes (for skybox rotation grouping)
   */
  setParentNode(parent: TransformNode | null): void {
    this.parentNode = parent;
    // Re-parent any existing model roots
    this.models.forEach((data) => {
      data.root.parent = parent;
    });
  }

  /**
   * Load and display a 3D model (additive - does not unload existing models)
   * Call unload() first if you want to replace all models
   * @param mediaItem - The media item containing model URL
   * @param onProgress - Optional progress callback
   * @param options - Optional load options
   * @param options.skipCentering - If true, don't auto-center/scale (for editor with manual transforms)
   */
  async load(
    mediaItem: MediaItem,
    onProgress?: (percent: number) => void,
    options?: { skipCentering?: boolean; skipStore?: boolean }
  ): Promise<{ root: TransformNode; meshes: AbstractMesh[] } | null> {
    // Check if this exact URL is already loaded (unless we're skipping store for generation check)
    if (!options?.skipStore && this.models.has(mediaItem.url)) {
      logger.debug('Model already loaded, skipping:', mediaItem.url);
      return null;
    }

    logger.info('Loading model:', mediaItem.title);

    try {
      // Load the model (lighting is now handled by SceneManager)
      const result = await this.mediaLoader.loadModel(mediaItem, onProgress);

      // Create a root transform node for this model
      const modelRoot = new TransformNode(`modelRoot_${this.models.size}`, this.scene);

      // Parent to scene content parent if set (for skybox rotation grouping)
      if (this.parentNode) {
        modelRoot.parent = this.parentNode;
      }

      // Parent all loaded meshes to the root
      result.meshes.forEach((mesh) => {
        mesh.parent = modelRoot;
      });

      // If skipStore is true, return data without storing (caller will decide whether to keep or dispose)
      if (options?.skipStore) {
        return { root: modelRoot, meshes: result.meshes };
      }

      // Store the model data
      const modelData: ModelData = {
        root: modelRoot,
        meshes: result.meshes,
        mediaItem: mediaItem,
      };

      this.models.set(mediaItem.url, modelData);
      this.lastLoadedUrl = mediaItem.url;

      // Center and scale the model (unless skipCentering is set for editor mode)
      if (!options?.skipCentering) {
        this.centerAndScaleModel(mediaItem.url);
      }

      logger.info('Model loaded successfully:', mediaItem.title, `(${this.models.size} total models)`);
      return { root: modelRoot, meshes: result.meshes };

    } catch (error) {
      logger.error('Failed to load model:', error);
      throw error;
    }
  }

  /**
   * Store a model that was loaded with skipStore option
   * Used for generation-checked loading where we load first, check, then decide to keep or discard
   */
  storeLoadedModel(url: string, root: TransformNode, meshes: AbstractMesh[], mediaItem: MediaItem): void {
    const modelData: ModelData = {
      root,
      meshes,
      mediaItem,
    };
    this.models.set(url, modelData);
    this.lastLoadedUrl = url;
    logger.info('Model stored:', url);
  }

  /**
   * Dispose a loaded model that wasn't stored (for discarding stale loads)
   */
  disposeLoadedModel(root: TransformNode, meshes: AbstractMesh[]): void {
    meshes.forEach((mesh) => mesh.dispose());
    root.dispose();
    logger.debug('Disposed unstored model');
  }

  /**
   * Center and scale a specific model to fit in view
   */
  private centerAndScaleModel(url: string): void {
    const modelData = this.models.get(url);
    if (!modelData || modelData.meshes.length === 0) return;

    // Calculate bounding box
    let min = new Vector3(Infinity, Infinity, Infinity);
    let max = new Vector3(-Infinity, -Infinity, -Infinity);

    modelData.meshes.forEach((mesh) => {
      const boundingInfo = mesh.getBoundingInfo();
      const meshMin = boundingInfo.boundingBox.minimumWorld;
      const meshMax = boundingInfo.boundingBox.maximumWorld;

      min = Vector3.Minimize(min, meshMin);
      max = Vector3.Maximize(max, meshMax);
    });

    // Calculate center and size
    const center = Vector3.Center(min, max);
    const size = max.subtract(min);
    const maxDimension = Math.max(size.x, size.y, size.z);

    // Center the model
    modelData.root.position = center.negate();

    // Scale to fit (target size ~2 units)
    const targetSize = 2;
    const scale = maxDimension > 0 ? targetSize / maxDimension : 1;
    modelData.root.scaling = new Vector3(scale, scale, scale);

    // Position in front of camera
    modelData.root.position.z = 3;
  }

  /**
   * Rotate a model around Y axis
   * @param angle Rotation angle in radians
   * @param url Optional URL to target specific model (defaults to last loaded)
   */
  rotateY(angle: number, url?: string): void {
    const modelData = this.getModelData(url);
    if (modelData) {
      modelData.root.rotation.y += angle;
    }
  }

  /**
   * Rotate a model around X axis
   * @param angle Rotation angle in radians
   * @param url Optional URL to target specific model (defaults to last loaded)
   */
  rotateX(angle: number, url?: string): void {
    const modelData = this.getModelData(url);
    if (modelData) {
      modelData.root.rotation.x += angle;
    }
  }

  /**
   * Scale a model
   * @param scaleFactor Factor to multiply current scale by
   * @param url Optional URL to target specific model (defaults to last loaded)
   */
  scale(scaleFactor: number, url?: string): void {
    const modelData = this.getModelData(url);
    if (modelData) {
      const currentScale = modelData.root.scaling.x;
      const newScale = currentScale * scaleFactor;
      modelData.root.scaling = new Vector3(newScale, newScale, newScale);
    }
  }

  /**
   * Reset model transform
   * @param url Optional URL to target specific model (defaults to last loaded)
   */
  resetTransform(url?: string): void {
    const targetUrl = url || this.lastLoadedUrl;
    if (targetUrl) {
      this.centerAndScaleModel(targetUrl);
    }
  }

  /**
   * Set the transform of a model directly without reloading
   * This is efficient for real-time updates from gizmos or input fields
   * @param url URL of the model to transform
   * @param position New position (optional)
   * @param rotation New rotation in radians (optional)
   * @param scale New scale as x,y,z or uniform number (optional)
   */
  setTransform(
    url: string,
    position?: { x: number; y: number; z: number },
    rotation?: { x: number; y: number; z: number },
    scale?: { x: number; y: number; z: number } | number
  ): boolean {
    const modelData = this.models.get(url);
    if (!modelData) {
      logger.warn('Model not found for transform update:', url);
      return false;
    }

    if (position) {
      modelData.root.position.set(position.x, position.y, position.z);
    }

    if (rotation) {
      modelData.root.rotation.set(rotation.x, rotation.y, rotation.z);
    }

    if (scale !== undefined) {
      if (typeof scale === 'number') {
        modelData.root.scaling.set(scale, scale, scale);
      } else {
        modelData.root.scaling.set(scale.x, scale.y, scale.z);
      }
    }

    logger.debug('Transform updated for:', url);
    return true;
  }

  /**
   * Get current media item for a model
   * @param url Optional URL to target specific model (defaults to last loaded)
   */
  getCurrentMediaItem(url?: string): MediaItem | null {
    const modelData = this.getModelData(url);
    return modelData?.mediaItem || null;
  }

  /**
   * Get all loaded meshes from all models
   */
  getMeshes(): AbstractMesh[] {
    const allMeshes: AbstractMesh[] = [];
    this.models.forEach((modelData) => {
      allMeshes.push(...modelData.meshes);
    });
    return allMeshes;
  }

  /**
   * Get meshes for a specific model
   * @param url URL of the model
   */
  getMeshesForModel(url: string): AbstractMesh[] {
    return this.models.get(url)?.meshes || [];
  }

  /**
   * Get model root node
   * @param url Optional URL to target specific model (defaults to last loaded)
   */
  getModelRoot(url?: string): TransformNode | null {
    const modelData = this.getModelData(url);
    return modelData?.root || null;
  }

  /**
   * Get all model root nodes
   */
  getAllModelRoots(): TransformNode[] {
    const roots: TransformNode[] = [];
    this.models.forEach((modelData) => {
      roots.push(modelData.root);
    });
    return roots;
  }

  /**
   * Get the number of loaded models
   */
  getModelCount(): number {
    return this.models.size;
  }

  /**
   * Check if a model is loaded
   * @param url URL of the model to check
   */
  isModelLoaded(url: string): boolean {
    return this.models.has(url);
  }

  /**
   * Unload a specific model by URL
   * @param url URL of the model to unload
   */
  unloadModel(url: string): void {
    const modelData = this.models.get(url);
    if (!modelData) return;

    // Dispose all meshes for this model
    modelData.meshes.forEach((mesh) => {
      mesh.dispose();
    });

    // Dispose the root node
    modelData.root.dispose();

    // Remove from map
    this.models.delete(url);

    // Update lastLoadedUrl if we just removed it
    if (this.lastLoadedUrl === url) {
      // Set to the last remaining model, or null if none
      const urls = Array.from(this.models.keys());
      this.lastLoadedUrl = urls.length > 0 ? urls[urls.length - 1] : null;
    }

    logger.info('Model unloaded:', url, `(${this.models.size} models remaining)`);
  }

  /**
   * Unload all models
   */
  unload(): void {
    // Dispose all models
    this.models.forEach((modelData) => {
      modelData.meshes.forEach((mesh) => {
        mesh.dispose();
      });
      modelData.root.dispose();
    });

    // Clear the map
    this.models.clear();
    this.lastLoadedUrl = null;

    logger.info('All models unloaded');
  }

  /**
   * Clear all models (alias for unload)
   */
  clear(): void {
    this.unload();
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.unload();
  }

  /**
   * Helper to get model data by URL or last loaded
   */
  private getModelData(url?: string): ModelData | null {
    if (url) {
      return this.models.get(url) || null;
    }
    if (this.lastLoadedUrl) {
      return this.models.get(this.lastLoadedUrl) || null;
    }
    return null;
  }
}
